import React from 'react';
import {
  WalkthroughBackupFileContents,
  WalkthroughPreviewEnrichment,
} from 'src/pages/Settings/components/WalkthroughBackupUploader/Hooks/useWalkthroughBackupUpload';
import Button from 'src/components/Button/Button';
import './FilePreviewCard.scss';
import { variables } from 'src/Tenants';

interface FilePreviewCardProps {
  file: File;
  contents: WalkthroughBackupFileContents;
  enrichment: WalkthroughPreviewEnrichment;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <div className="fpc_row">
    <span className="fpc_row_label">{label}</span>
    <span className="fpc_row_value">{value}</span>
  </div>
);

export const FilePreviewCard = ({
  file,
  contents,
  enrichment,
  onConfirm,
  onCancel,
}: FilePreviewCardProps) => (
  <div className="fpc_container">
    {/* File name + change link. */}
    <div className="fpc_file_bar">
      <div className="fpc_file_chip">
        <span className="fpc_file_icon">📄</span>
        <span className="fpc_file_name">{file?.name}</span>
      </div>
      <Button label="Change file" onClick={onCancel} size="small" outlined />
    </div>
    {/* Extracted details table. */}
    <div className="fpc_details">
      <div className="fpc_details_header">
        <span className="fpc_details_title">Extracted Walkthrough Details</span>
        <span className="fpc_validation_badge">✓ File verified</span>
      </div>
      <DetailRow
        label="Walkthrough"
        value={
          <span className="fpc_row_value_stack">
            <span className="fpc_row_value_name">
              {enrichment?.walkthroughName}
            </span>
            <span className="fpc_row_value_uuid">
              {contents?.walkthroughId}
            </span>
          </span>
        }
      />
      <DetailRow
        label="Property"
        value={
          <span className="fpc_row_value_stack">
            <span className="fpc_row_value_name">
              {enrichment?.propertyName}
            </span>
            <span className="fpc_row_value_uuid">{contents?.propertyId}</span>
          </span>
        }
      />
      <DetailRow
        label="Suite"
        value={
          <span className="fpc_row_value_stack">
            <span className="fpc_row_value_name">
              {variables.Suite} {enrichment?.suiteNumber}
            </span>
            <span className="fpc_row_value_uuid">{contents?.suiteId}</span>
          </span>
        }
      />
      <DetailRow
        label="Status"
        value={
          <span className="fpc_status_badge">
            {enrichment?.walkthroughStatusFromAPI}
          </span>
        }
      />
      <DetailRow label="File size" value={formatFileSize(file?.size)} />
      <DetailRow
        label="Last modified"
        value={formatDate(new Date(file?.lastModified))}
      />
    </div>
    {/* Advisory note. */}
    <p className="fpc_advisory">
      Please review the details above carefully. Once confirmed, the backup file will be uploaded and queued for restore.
    </p>
    {/* Actions. */}
    <div className="fpc_actions">
      <Button
        label="Confirm & Begin Upload"
        size="small"
        color="teal"
        onClick={onConfirm}
        block
      />
      <Button label="Cancel" size="small" onClick={onCancel} outlined />
    </div>
  </div>
);
